/**
 * Sync queue processor: drains IndexedDB pending actions when back online.
 * Replays requests oldest-first, with exponential backoff on server errors.
 */
import axios from 'axios';
import { getPendingActions, removeAction, updateAction, PendingAction } from './offlineDb';

const MAX_RETRIES = 10;
let syncing = false;

async function drainQueue() {
  if (syncing) return;
  syncing = true;
  try {
    const actions = await getPendingActions();
    for (const action of actions) {
      if (!navigator.onLine) break;
      try {
        await axios({
          method: action.method.toLowerCase(),
          url: action.url,
          data: action.body,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
            ...action.headers,
          },
        });
        await removeAction(action.id);
      } catch (err: any) {
        if (!err.response) break; // Network still down — stop processing
        if (action.retries >= MAX_RETRIES) continue; // Skip permanently failed actions
        // Server error — increment retry, apply backoff, move to next
        await updateRetry(action);
        const backoff = Math.min(2 ** action.retries * 1000, 60000);
        await new Promise(r => setTimeout(r, backoff));
      }
    }
  } finally {
    syncing = false;
  }
}

async function updateRetry(action: PendingAction) {
  await updateAction({ ...action, retries: action.retries + 1 });
}

/** Register Background Sync so the SW can drain the queue even if the tab is closed */
export async function requestBackgroundSync() {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    const reg = await navigator.serviceWorker.ready;
    await (reg as any).sync.register('tnb-offline-sync');
  }
}

/** Call once on app boot to start listening for online events */
export function startSyncQueue() {
  window.addEventListener('online', () => drainQueue());
  // Also drain immediately in case there are pending actions from a previous session
  if (navigator.onLine) drainQueue();
}
